import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * How close to the end of the page counts as being at the bottom, in pixels. Roughly one
 * phone screen, so a shopper who stops just short of the footer still pulls the next batch.
 */
const BOTTOM_SLACK = 900;

/**
 * Pause between consecutive batches, in milliseconds. Shopify answers section renders with
 * 429 when they arrive too quickly, and a batch is a 200 KiB render, so batches are spaced
 * rather than chained back to back.
 */
const BATCH_SPACING = 600;

/**
 * How long to wait before another go at a batch that did not arrive, in milliseconds.
 *
 * A shopper already at the end of the page has no scrolling left to prompt another attempt,
 * so these are the only thing that can recover it, and they carry on for about half a minute
 * before the page settles for what it has.
 */
const RETRY_DELAYS = [800, 2000, 4000, 8000, 15000];

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} Whether the next batch has already been warmed */
  #warmed = false;

  /** @type {boolean} Whether a scroll check is already waiting on a frame */
  #scrollQueued = false;

  /**
   * @type {boolean} Whether a next batch is mid-render.
   *
   * Both the observer and the bottom-of-page check can ask for the next batch, and the ask
   * is answered after an await. Without this they can both append the same page.
   */
  #appending = false;

  /** @type {number} Attempts made at a batch that has not arrived */
  #retries = 0;

  /**
   * @type {number} The highest page appended so far.
   *
   * Both the observer and the bottom check work out what to load next from the last card in
   * the grid, and that ref list is updated by a mutation observer, so just after an append it
   * can still describe the batch before. This is the authoritative record.
   */
  #highestPage = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // IntersectionObserver + rootMargin prefetch when items approach the viewport.
    this.#observeViewMore();

    // Fetching the next batch only once the shopper is nearly at the end of this one means
    // a fast scroll arrives before the products do and lands on the footer. Warming the
    // next batch early costs one request and no rendering, and it is what makes the first
    // flick down feel instant.
    this.#prefetchNext();

    this.#watchForBottom();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    window.removeEventListener('scroll', this.#handleScroll);
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // About three phone screens of lead time below the viewport, so a batch is on its
          // way long before the shopper reaches the end of the current one. The top margin
          // stays small: scrolling back up is rare and prepending has to adjust the scroll
          // position, which is better done close to the viewport.
          rootMargin: '200px 0px 2500px 0px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Warms the next batch at the first idle moment, or as soon as the shopper starts moving.
   *
   * Deliberately not waiting for the window load event: on a collection page that waits for
   * every app script, by which time a shopper can already be at the bottom. The idle timeout
   * is past this page's largest paint, so it does not compete with the first images, and the
   * scroll trigger covers anyone who swipes before then.
   */
  #prefetchNext() {
    // Only infinite scroll renders this marker, and only it consumes prefetched pages.
    if (!this.refs.viewMoreNext) return;

    const warm = () => {
      if (this.#warmed) return;
      this.#warmed = true;
      this.#fetchPage('next');
    };

    requestIdleCallback(warm, { timeout: 2000 });
    window.addEventListener('scroll', warm, { passive: true, once: true });
  }

  /**
   * Keeps loading while the shopper sits at the bottom of the page.
   *
   * The marker above is behind them by then, and a root margin only reaches downwards, so the
   * observer has nothing left to report and the page would stop a batch or two in - which is
   * what let a hard swipe end on the footer with most of the collection never loading.
   */
  #watchForBottom() {
    if (!this.refs.viewMoreNext) return;

    window.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #handleScroll = () => {
    if (this.#scrollQueued) return;
    this.#scrollQueued = true;

    requestAnimationFrame(() => {
      this.#scrollQueued = false;
      this.#maybeLoadMore();
    });
  };

  /**
   * Loads the next batch if the end of the page is within reach and batches remain.
   *
   * Runs after every append as well as on scroll. A shopper who has reached the bottom
   * produces no further scroll events however hard they keep swiping, and the marker above
   * them is behind the observer's reach, so this is the only thing that carries the page
   * through the rest of a collection once they are down there.
   */
  #maybeLoadMore() {
    const { grid } = this.refs;
    if (!grid) return;

    // The ref list is kept in step by a mutation observer, so just after an append the last
    // card can still describe the batch before. Whichever is further along is the truth.
    const loaded = Math.max(this.#loadedPage(), this.#highestPage);
    const remaining = Number(grid.dataset.lastPage || 1) - loaded;
    if (remaining < 1) return;

    const fromBottom = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
    if (fromBottom < BOTTOM_SLACK) this.#renderNextPage();
  }

  /**
   * @returns {number} The page number of the last card in the grid
   */
  #loadedPage() {
    const { cards } = this.refs;

    return Array.isArray(cards) ? Number(cards[cards.length - 1]?.dataset.page || 1) : 1;
  }

  /**
   * Re-delivers the next marker's current position to the observer.
   *
   * An observer only reports changes. After appending a batch the marker usually still sits
   * inside the root margin, so it stays intersecting, never reports again, and loading stops
   * for good - which is how the page used to stall a couple of batches in with the footer in
   * reach. Re-arming reports the current state, which either loads the following batch right
   * away or waits for the next scroll.
   */
  #rearmNextObserver() {
    const { viewMoreNext } = this.refs;

    if (!viewMoreNext || !this.infinityScrollObserver) return;

    this.infinityScrollObserver.unobserve(viewMoreNext);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * Another go at a batch that did not arrive, backing off each time and giving up after a
   * couple of attempts so a genuinely broken page does not request forever.
   */
  #retryLater() {
    const delay = RETRY_DELAYS[this.#retries];

    if (delay == null) {
      // Out of attempts. Placeholder cards promise more is on its way, so with nothing left
      // coming they have to go: the page then ends on the last real card, which is how it
      // behaved before any of this, rather than on a row of skeletons that never fill in.
      this.querySelector('[data-grid-skeleton]')?.remove();
      return;
    }

    this.#retries += 1;
    setTimeout(() => this.#renderNextPage(), delay);
  }

  /**
   * The batch to load next, worked out without consulting the ref list.
   *
   * That list is kept in step by a mutation observer, so for a moment after an append the
   * last card still describes the batch before it. Reading the next page from it asks for a
   * batch that has already been appended, and the page then has nothing left to do and stops
   * with placeholder cards showing. Counting from whichever record is further along always
   * moves forward.
   *
   * @returns {{ page: number, url: URL } | undefined}
   */
  #nextPageInfo() {
    const page = Math.max(this.#loadedPage(), this.#highestPage) + 1;
    const url = new URL(window.location.href);

    url.searchParams.set('page', page.toString());
    url.hash = '';

    const info = { page, url };

    return this.#shouldUsePage(info) ? info : undefined;
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    // Same reason as in #appendNextPage: warming from the ref list re-requests a batch that
    // is already on the page.
    const page = type === 'next' ? this.#nextPageInfo() : this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // A render that failed, usually rate limited, is left for the retry in #appendNextPage.
    } finally {
      // Always, or a render that failed leaves whoever is waiting on it waiting for good.
      resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    // An ask that lands mid-render is dropped rather than queued: the check that runs once
    // this append finishes will ask again if the batch is still wanted.
    if (this.#appending) return;

    this.#appending = true;

    let appended = 0;

    try {
      appended = await this.#appendNextPage();
    } finally {
      this.#appending = false;
    }

    // Carry on while the end of the page is still in reach, spaced to stay under the rate
    // limit. This also covers an ask that arrived mid-render and was dropped above: by the
    // time this runs the batch has landed, and the check asks again if it is still wanted.
    // Guarded on something arriving, so a batch that brings nothing cannot become a loop -
    // that case is left to #retryLater instead.
    if (appended > 0) setTimeout(() => this.#maybeLoadMore(), BATCH_SPACING);
  }

  /**
   * @returns {Promise<number>} How many cards were appended
   */
  async #appendNextPage() {
    const { grid } = this.refs;

    if (!grid || this.hasAttribute('searching')) return 0;

    const nextPage = this.#nextPageInfo();

    if (!nextPage) return 0;

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      // Awaited directly, for this exact page, rather than parking a resolver in a shared
      // field and waiting on it. A warm started after the previous batch can land while this
      // one is waiting, and it resolved this wait too - so the batch was looked for before
      // its own render had arrived, and the page gave up with placeholders still showing.
      // The section renderer de-duplicates requests for a URL already in flight, so asking
      // again here costs nothing.
      try {
        await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      } catch {
        // Usually rate limiting; left to the retry below.
      }

      nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements || this.hasAttribute('searching')) {
        // Nothing arrived, so the shopper is looking at the end of what has loaded with no
        // scroll left to prompt another go. Rate limiting clears in a moment, so try again.
        this.#retryLater();
        return 0;
      }
    }

    const appended = nextPageItemElements.length;

    if (appended > 0) {
      this.#highestPage = nextPage.page;
      this.#retries = 0;
    }

    grid.append(...nextPageItemElements);

    // Defer aspect ratio processing to avoid blocking
    requestIdleCallback(() => {
      this.#aspectRatioHelper?.processNewElements();
    }, { timeout: 500 });

    // FIXED: Don't update URL to prevent carousel pagination issues
    // history.pushState('', '', nextPage.url.toString());

    // Warm the batch after this one, spaced for the same reason as the re-arm below: this is
    // what used to chain renders back to back and earn a 429. The idle call needs a deadline
    // of its own - a phone mid-scroll never goes idle, and without one this never ran.
    setTimeout(() => requestIdleCallback(() => this.#fetchPage('next'), { timeout: 1000 }), BATCH_SPACING);

    // Guarded on something actually arriving: an empty batch would leave the last card's
    // page number unchanged, and re-arming would then ask for the same page forever. Spaced
    // out so a shopper holding at the end of the page does not pull batches back to back and
    // get rate limited.
    if (appended > 0) setTimeout(() => this.#rearmNextObserver(), BATCH_SPACING);

    // Nothing is queued up behind the last batch, so the placeholder buffer that keeps the
    // footer out of reach has served its purpose. Read from the page just rendered rather
    // than from refs, which the mutation observer has not caught up with yet.
    if (nextPage.page >= Number(grid.dataset.lastPage || 1)) {
      this.querySelector('[data-grid-skeleton]')?.remove();
    }

    return appended;
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.hasAttribute('searching')) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements || this.hasAttribute('searching')) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    // Defer aspect ratio processing to avoid blocking
    requestIdleCallback(() => {
      this.#aspectRatioHelper?.processNewElements();
    }, { timeout: 500 });

    // FIXED: Don't update URL to prevent carousel pagination issues
    // history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Filtered results start again from page one, so the record of what has been appended
    // has to go with the cache, or the first batch would look like one already seen.
    this.#highestPage = 0;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
